import React, { useEffect, useState } from 'react'
import AuthLink from '../../Auth/AuthLink'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import {
  MapPin, Calendar, ArrowRight, TrendingUp,
  Building2, Gamepad2, PenLine, Archive, Trophy, HelpCircle,
  Brain, Swords, Layers, LucideIcon, Users, User, ExternalLink,
  CheckCircle, Check, Shuffle, Eye, EyeOff, Link as LinkIcon
} from 'lucide-react'

import api from '../../../const/axios'
import siteUrl from '../../../const/const'
import { isFeatureEnabled } from '../../../services/flagsService'
import { TOOL_TONES, Tool, homeTools } from '../../../const/tools'
import { getHomePackages, getStatistics } from '../../../redux/actions'
import Package from '../../Elements/Package/Package'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn, decodeEntities, safeExternalUrl } from '@/lib/utils'
import RandomPackageCountPicker from '../RandomPackage/RandomPackageCountPicker'
import ComingSoonTicker from './ComingSoonTicker'
import TopAuthorsCarousel, { TopAuthors } from './TopAuthorsCarousel'

import imgs from '../../../assets/img/img'
import gyf from '../Tournaments/components/assets/gyf.jpeg'
import alohaWorld from '../Tournaments/components/assets/aloha-world.png'
import { RichTextView } from '../TournamentPanel/Appeals/shared'

// Question text uses `/-/` as a line separator (see Package/Single/Question.tsx).
const TextLines: React.FC<{ text: string; indent?: boolean }> = ({ text, indent }) =>
  <>
    {(text ?? '').split('/-/').map((m, i) => (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {indent && i > 0 ? <span>&nbsp;&nbsp;</span> : null}
        {m}
      </React.Fragment>
    ))}
  </>

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string | Date) => {
  const dt = new Date(d)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(dt.getDate())}.${p(dt.getMonth() + 1)}.${dt.getFullYear()}, ${p(dt.getHours())}:${p(dt.getMinutes())}`
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface StatCardProps {
  count: number | string | undefined
  label: string
  icon: LucideIcon
  color: string
  iconColor: string
  wide?: boolean
  accent?: boolean
}

const StatCard: React.FC<StatCardProps> = ({ count, label, icon: Icon, color, iconColor, wide, accent }) => (
  <div className={cn(
    "group rounded-2xl p-5 flex flex-col gap-3 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg",
    accent
      ? "bg-gradient-to-br from-blue-600 to-indigo-700 shadow-md shadow-blue-200 dark:shadow-blue-900/30 text-white"
      : "bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm",
    wide && "col-span-2"
  )}>
    <div className={cn(
      "w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-200 group-hover:scale-110",
      accent ? "bg-white/15" : color
    )}>
      <Icon className={cn("w-5 h-5", accent ? "text-white" : iconColor)} strokeWidth={2} />
    </div>
    <span className={cn(
      "text-3xl font-black tracking-tight leading-none",
      accent ? "text-white" : "text-gray-900 dark:text-gray-50"
    )}>
      {count ?? 0}
    </span>
    <span className={cn(
      "text-xs font-semibold tracking-wider leading-tight",
      accent ? "text-blue-100" : "text-gray-400 dark:text-gray-500"
    )}>
      {label}
    </span>
  </div>
)

interface SectionTitleProps {
  children: React.ReactNode
  action?: React.ReactNode
}
const SectionTitle: React.FC<SectionTitleProps> = ({ children, action }) => (
  <div className="flex items-center justify-between mb-6">
    <div className="flex items-center gap-3">
      <div className="w-1 h-6 rounded-full bg-blue-500" />
      <h2 className="text-sm sm:text-xl font-bold text-gray-900 dark:text-gray-50">{children}</h2>
    </div>
    {action}
  </div>
)

// ─── Main component ───────────────────────────────────────────────────────────

// `!rounded-full` + appearance-none: Reboot.css resets border-radius/appearance on <button>
// Icon-only 40px circles on mobile, icon + label pills from `sm` up
const toolChipClass = 'group appearance-none inline-flex flex-shrink-0 items-center justify-center gap-1.5 !rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 h-10 w-10 sm:h-auto sm:w-auto sm:px-3 sm:py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-800'

const ToolChip: React.FC<{ tool: Tool; onClick?: () => void }> = ({ tool, onClick }) => {
  const Icon = tool.icon
  const content = (
    <>
      <Icon className={cn('h-5 w-5 sm:h-4 sm:w-4', TOOL_TONES[tool.tone].chip)} />
      {/* sr-only keeps the name as the accessible label when only the icon shows */}
      <span className="sr-only sm:not-sr-only">{tool.short}</span>
    </>
  )
  return onClick
    ? <button type="button" onClick={onClick} title={tool.description} className={toolChipClass}>{content}</button>
    : <Link to={tool.to} rel="nofollow" title={tool.description} className={toolChipClass}>{content}</Link>
}

const PARTNERS = [
  { img: alohaWorld, url: 'https://www.instagram.com/alohaworld.az/' },
  { img: gyf, url: 'https://gyf.org.az/' },
]

const Home: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const packages = useSelector((state: any) => state.packages)
  const stats = useSelector((state: any) => state.statistics)
  const user = useSelector((state: any) => state.user)
  const navigate = useNavigate()
  const [events, setEvents] = useState<any[]>([])
  const [randomPackageOpen, setRandomPackageOpen] = useState(false)

  // Random question
  const [randomQ, setRandomQ] = useState<any | null | undefined>(undefined) // undefined = loading
  const [questionCopied, setQuestionCopied] = useState(false)
  // undefined = loading, null = failed (card hidden)
  const [topAuthors, setTopAuthors] = useState<TopAuthors | null | undefined>(undefined)
  const [answerVisible, setAnswerVisible] = useState(false)
  const [shuffleCooldown, setShuffleCooldown] = useState(0)
  const SHUFFLE_COOLDOWN_SEC = 20

  // Event detail / registration
  const [detailEvent, setDetailEvent] = useState<any | null>(null)
  const [registerTeamId, setRegisterTeamId] = useState<number | null>(null)
  const [myTeams, setMyTeams] = useState<any[]>([])
  const [isRegistered, setIsRegistered] = useState(false)
  const [registrationLoading, setRegistrationLoading] = useState(false)
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null)
  const upcomingEvents = events.filter((event) => {
    if (!event?.date) return false
    const eventTs = new Date(event.date).getTime()
    return Number.isFinite(eventTs) && eventTs >= Date.now()
  })

  const showRandomQuestion = isFeatureEnabled('HOME_RANDOM_QUESTION') && randomQ !== null
  // Duplet/blits parts come back together; older API responses have no `parts`.
  const randomParts: any[] = randomQ?.parts ?? []
  const randomPartsLabel = ({ 2: 'Duplet', 3: 'Blits', 4: 'Kvadriplet' } as Record<number, string>)[randomParts.length]
  const launcherTools = homeTools()
  const showTopAuthors = topAuthors !== null
    && (topAuthors === undefined || Object.values(topAuthors).some(list => list?.length > 0))
  const showEvents = isFeatureEnabled('PROFILE_EVENTS')

  const fetchRandomQuestion = () => {
    if (!isFeatureEnabled('HOME_RANDOM_QUESTION')) return
    api.get('questions/random-question')
      .then(res => {
        setRandomQ(res.data)
        setAnswerVisible(false)
      })
      .catch(() => setRandomQ(null))
  }

  const handleShuffle = () => {
    if (shuffleCooldown > 0) return
    setShuffleCooldown(SHUFFLE_COOLDOWN_SEC)
    fetchRandomQuestion()
  }

  useEffect(() => {
    dispatch(getHomePackages() as any)
    dispatch(getStatistics() as any)
    if (isFeatureEnabled('PROFILE_EVENTS')) {
      api.get('events').then(res => setEvents(res.data))
    }
    fetchRandomQuestion()
    api.get('authors/top?take=5')
      .then(res => setTopAuthors(res.data))
      .catch(() => setTopAuthors(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  useEffect(() => {
    if (shuffleCooldown <= 0) return
    const id = setInterval(() => setShuffleCooldown(s => (s <= 1 ? 0 : s - 1)), 1000)
    return () => clearInterval(id)
  }, [shuffleCooldown])

  // When event detail opens, check registration status + load user teams + fetch registrations
  useEffect(() => {
    if (!detailEvent) return
    setIsRegistered(false)
    setRegisterTeamId(null)

    if (user.isLoggedIn) {
      api.get(`events/${detailEvent.id}/myregistration`)
        .then(res => {
          setIsRegistered(res.data.isRegistered)
          if (res.data.isRegistered && res.data.registration?.team) {
            setRegisterTeamId(res.data.registration.team.id)
          }
        })
        .catch(() => {})

      if (detailEvent.gameType === 'team') {
        api.get('users/profile')
          .then(res => setMyTeams(res.data.teams || []))
          .catch(() => {})
      }
    }
  }, [detailEvent, user.isLoggedIn])

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok })
    setTimeout(() => setToast(null), 3000)
  }

  const handleRegister = async () => {
    if (!detailEvent) return
    if (!user.isLoggedIn) return
    if (detailEvent.gameType === 'team' && !registerTeamId) {
      showToast('Komanda seçin', false)
      return
    }
    setRegistrationLoading(true)
    try {
      const body = detailEvent.gameType === 'team' ? { teamId: registerTeamId } : {}
      const res = await api.post(`events/${detailEvent.id}/register`, JSON.stringify(body))
      if (res.data.success) {
        showToast(res.data.message, true)
        setIsRegistered(true)
      } else {
        showToast(res.data.message, false)
      }
    } catch {
      showToast('Xəta baş verdi', false)
    } finally {
      setRegistrationLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>{t('home.title')}</title>
        <meta name="description" content={t('home.metaDescription')} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="3sual.az" />
        <meta property="og:url" content="https://3sual.az" />
        <meta property="og:title" content={t('home.title')} />
        <meta property="og:description" content={t('home.metaDescription')} />
        <link rel="canonical" href="https://3sual.az/" />
      </Helmet>

      {/* Inline toast */}
      {toast && (
        <div className={cn(
          "fixed top-20 right-4 z-[200] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg border text-sm font-semibold",
          toast.ok ? "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200" : "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
        )}>
          {toast.ok ? <CheckCircle className="w-4 h-4" /> : null}
          {toast.msg}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">

        {/* ── Coming soon ticker ───────────────────────────────────────── */}
        {isFeatureEnabled('HOME_COMING_SOON_FEED') && <ComingSoonTicker />}

        {/* ── Ad banner ─────────────────────────────────────────────────── */}
        {isFeatureEnabled('HOME_AD_BANNER') && (
        <div className="w-full border border-dashed border-gray-300 dark:border-gray-700 rounded-2xl bg-gradient-to-r from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 py-5 px-4 flex items-center justify-center group">
          <div className="flex flex-col items-center gap-1 text-center select-none">
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-gray-300 dark:text-gray-600">Reklam</span>
            <p className="text-lg sm:text-2xl font-black text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500 transition-colors duration-300 tracking-tight">
              Sizin reklamınız burada ola bilər
            </p>
            <span className="text-xs text-gray-300 dark:text-gray-600 font-medium">728 × 90 · info@3sual.az</span>
          </div>
        </div>
        )}

        {/* ── Events ──────────────────────────────────────────────────── */}
        {showEvents && upcomingEvents.length > 0 && (
          <section>
            <SectionTitle>Tədbirlər</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {upcomingEvents.map(event => (
                <Card
                  key={event.id}
 className="group overflow-hidden border-gray-100 dark:border-gray-800 hover:shadow-xl transition-all duration-300 flex flex-row h-[100px] cursor-pointer"
                  onClick={() => setDetailEvent(event)}
                >
                  <div className="relative w-28 sm:w-36 flex-shrink-0 overflow-hidden border-r border-gray-50 dark:border-gray-800">
                    <img
                      src={event.media ? `${siteUrl}images/${event.media.folder || 'users'}/${event.media.name}` : imgs.header}
                      alt={event.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <CardContent className="p-3 flex flex-col justify-center min-w-0 flex-1">
                    <h3 className="font-bold text-gray-900 dark:text-gray-100 line-clamp-1 group-hover:text-blue-500 transition-colors leading-snug mb-1 dark:group-hover:text-indigo-200">
                      {event.name}
                    </h3>
                    <div className="flex flex-col gap-1.5 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-blue-500/60" />
                        {fmtDate(event.date)}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-blue-500/60 flex-shrink-0" />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* ── Tools launcher ─────────────────────────────────────────── */}
        {launcherTools.length > 0 && (
          <div className="flex items-center gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {launcherTools.map(tool => (
                <ToolChip
                  key={tool.id}
                  tool={tool}
                  // Random package asks for the question count first instead of jumping to the default
                  onClick={tool.id === 'random-package' ? () => setRandomPackageOpen(true) : undefined}
                />
              ))}
            </div>
            <Link
              to="/tools"
              className="inline-flex flex-shrink-0 items-center gap-1 text-sm font-semibold text-blue-500 hover:text-blue-600 dark:text-indigo-300 dark:hover:text-indigo-200"
            >
              Bütün alətlər <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        {/* ── Daily Random Question + top authors ───────────────────── */}
        {(showRandomQuestion || showTopAuthors) && (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 items-stretch">
          {showRandomQuestion && (
          <div className="w-full h-full">
            {randomQ === undefined ? (
              <div className="h-full animate-pulse flex gap-3 items-start bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm rounded-2xl px-4 py-3">
                <div className="flex-1 space-y-2 pt-0.5">
                  <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full w-1/3" />
                  <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full w-full" />
                  <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full w-2/3" />
                </div>
              </div>
            ) : (
              <div className="h-full bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm rounded-2xl px-4 py-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-500 dark:text-indigo-300">
                    <Shuffle className="w-3 h-3" /> Təsadüfİ sual
                  </span>
                  <button
                    onClick={handleShuffle}
                    disabled={shuffleCooldown > 0}
                    className={cn(
                      "flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors",
                      shuffleCooldown > 0
                        ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        : "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800"
                    )}
                    title={shuffleCooldown > 0 ? `${shuffleCooldown} saniyə gözləyin` : 'Yenİ təsadüfi sual'}
                  >
                    <Shuffle className={cn("w-3 h-3", shuffleCooldown === 0 && "group-hover:rotate-180 transition-transform")} />
                    {shuffleCooldown > 0 ? `${shuffleCooldown}s` : 'Dəyişdir'}
                  </button>
                </div>
                {randomQ.package && (
                  <Link
                    to={`/packages/${randomQ.package.id}`}
                    className="block text-[11px] font-semibold text-blue-500 hover:underline dark:text-indigo-300"
                  >
                    {randomQ.package.name}
                  </Link>
                )}
                {randomQ.noteBefore && (
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{randomQ.noteBefore}</p>
                )}
                {randomParts.length > 1 ? (
                  <div className="space-y-0.5">
                    {randomPartsLabel && (
                      <p className="text-[11px] font-bold text-blue-500 dark:text-indigo-300">{randomPartsLabel}</p>
                    )}
                    {randomParts.map((part: any, i: number) => (
                      <p key={part.id ?? i} className="text-sm text-gray-800 dark:text-gray-200 leading-snug">
                        {i + 1}. <TextLines text={part.text} indent />
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug"><TextLines text={randomQ.text} /></p>
                )}
                {randomQ.authors?.length > 0 && (
                  <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    <span className="font-semibold text-gray-500 dark:text-gray-400">Müəllif:</span>{" "}
                    {randomQ.authors.join(', ')}
                  </p>
                )}
                <div className="flex items-start gap-2 pt-0.5">
                  <div className="flex-1 min-w-0">
                    {answerVisible && (
                      <div className="space-y-0.5">
                        <p className="text-xs font-bold text-blue-700 dark:text-indigo-300">
                          Cavab:{" "}
                          {randomParts.length > 1
                            ? randomParts.map((part: any, i: number) => `${i + 1}. ${part.answer}`).join(' ')
                            : randomQ.answer}
                        </p>
                        {randomQ.considered && (
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                            <span className="font-semibold">Sayılma meyarı:</span> {randomQ.considered}
                          </p>
                        )}
                        {randomQ.comment && (
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-snug">{randomQ.comment}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => setAnswerVisible(v => !v)}
                      className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors",
                        answerVisible
                          ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                      )}
                    >
                      {answerVisible ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      {answerVisible ? 'Gizlət' : 'Cavab'}
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/questions/${randomQ.id}`)
                          .then(() => { setQuestionCopied(true); setTimeout(() => setQuestionCopied(false), 2000) })
                      }}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      <LinkIcon className="w-3 h-3" />
                      {questionCopied ? 'Kopyalandı!' : 'Paylaş'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}
            {showTopAuthors && <TopAuthorsCarousel data={topAuthors ?? undefined} />}
          </div>
        )}

        {/* ── Latest Packages ──────────────────────────────────────────────── */}
        <section>
          <SectionTitle
            action={
              <Button asChild variant="ghost" size="sm" className="text-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:text-indigo-300 dark:hover:bg-blue-950 dark:hover:text-indigo-200">
                <Link to="/packages">
                  Hamısına bax <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            }
          >
            {t('home.latestPackages')}
          </SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map((item: any, index: number) => (
              <Link key={index} to={`/packages/${item.id}`} className="block">
                <Package pack={item} free={true} />
              </Link>
            ))}
          </div>
        </section>

        {/* ── Statistics ───────────────────────────────────────────────── */}
        <section>
          <SectionTitle>
            <span className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500 dark:text-indigo-300" />
              {t('home.statistics')}
            </span>
          </SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard count={stats.organizations} label={t('home.organization')}    icon={Building2}   color="bg-purple-100"  iconColor="text-purple-600" />
            <StatCard count={stats.games}         label={t('home.gameType')}        icon={Gamepad2}    color="bg-pink-100"    iconColor="text-pink-600" />
            <StatCard count={stats.authors}       label={t('home.author')}          icon={PenLine}     color="bg-amber-100"   iconColor="text-amber-600" />
            <StatCard count={stats.packages}      label={t('home.questionPackage')} icon={Archive}     color="bg-emerald-100" iconColor="text-emerald-600" />
            <StatCard count={stats.tournaments}   label={t('home.tournament')}      icon={Trophy}      color="bg-yellow-100"  iconColor="text-yellow-600" />
            <StatCard count={`~${stats.theme * 5}`}     label={t('home.topicsWithQuestions')} icon={Layers}  color="bg-indigo-100"  iconColor="text-indigo-600" />
            <StatCard count={stats.br}            label={t('home.brainRing')}       icon={Brain}       color="bg-rose-100"    iconColor="text-rose-600" />
            <StatCard count={stats.xamsa}         label={t('home.decisiveRound')}   icon={Swords}      color="bg-orange-100"  iconColor="text-orange-600" />
            <StatCard count={stats.nhn}           label={t('home.whatWhereWhen')}   icon={HelpCircle}  color="bg-sky-100"     iconColor="text-sky-600"  accent  wide />
          </div>
        </section>

        {isFeatureEnabled('HOME_PARTNERS') && (
        <>
        <Separator />

        {/* ── Partners ─────────────────────────────────────────────────── */}
        <section>
          <SectionTitle>{t('home.partners')}</SectionTitle>
          <div className="flex flex-wrap gap-4 items-center">
            {PARTNERS.map((p, i) => (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
 className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl p-4 h-[70px] w-[150px] flex items-center justify-center grayscale hover:grayscale-0 hover:shadow-md transition-all duration-300"
              >
                <img alt="partner logo" className="max-h-full max-w-full object-contain" src={p.img} />
              </a>
            ))}
          </div>
        </section>
        </>
        )}

      </div>

      {/* ── Random package dialog ─────────────────────────────────────── */}
      <Dialog open={randomPackageOpen} onOpenChange={setRandomPackageOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Təsadüfi paket</DialogTitle>
          </DialogHeader>
          <RandomPackageCountPicker
            onSubmit={count => {
              setRandomPackageOpen(false)
              navigate(`/packages/random?count=${count}`)
            }}
          />
        </DialogContent>
      </Dialog>

      {/* ── Event detail / registration modal ──────────────────────────── */}
      <Dialog open={!!detailEvent} onOpenChange={v => { if (!v) { setDetailEvent(null); setRegisterTeamId(null); setMyTeams([]); } }}>
        <DialogContent className="max-w-md">
          {detailEvent && (
            <>
              {/* Banner */}
              {/* {detailEvent.media && (
                <div className="relative -mx-6 -mt-6 mb-4 h-44 overflow-hidden rounded-t-2xl">
                  <img
                    src={`${siteUrl}images/${detailEvent.media.folder || 'users'}/${detailEvent.media.name}`}
                    alt={detailEvent.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </div>
              )} */}

              <DialogHeader>
                <DialogTitle className="text-lg">{detailEvent.name}</DialogTitle>
              </DialogHeader>

              <div className="space-y-3 mt-1">
                {/* Meta */}
                <div className="flex flex-col gap-2 text-sm text-gray-600 dark:text-gray-300">
                  {detailEvent.date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0 dark:text-indigo-300" />
                      {fmtDate(detailEvent.date)}
                    </div>
                  )}
                  {detailEvent.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0 dark:text-indigo-300" />
                      {detailEvent.location}
                    </div>
                  )}
                  {detailEvent.fee != null && detailEvent.fee > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center text-blue-500 font-bold text-xs dark:text-indigo-300">₼</span>
                      {detailEvent.fee} AZN iştirak haqqı
                    </div>
                  )}
                  {detailEvent.gameType && (
                    <div className="flex gap-2 mt-1">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold dark:text-indigo-300">
                        {detailEvent.gameType === 'team'
                          ? <><Users className="w-3 h-3" /> Komanda</>
                          : <><User className="w-3 h-3" /> Fərdi</>}
                      </span>
                    </div>
                  )}
                </div>

                {detailEvent.information && (
                  <RichTextView
                    className="text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800 pt-3"
                    html={decodeEntities(detailEvent.information)}
                  />
                )}

                <Separator />

                {/* Registration */}
                {detailEvent.registrationType === 'link' && detailEvent.registrationLink ? (
                  <a href={safeExternalUrl(detailEvent.registrationLink)} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full gap-2">
                      <ExternalLink className="w-4 h-4" />
                      Qeydiyyat linkə keç
                    </Button>
                  </a>
                ) : (detailEvent.registrationType === 'button' || !detailEvent.registrationType) && !detailEvent.registrationLink ? (
                  <div className="space-y-3">
                    {isRegistered ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-xl text-green-700 dark:text-green-300 text-sm font-semibold">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        Qeydiyyatdan keçmisiniz
                      </div>
                    ) : user.isLoggedIn ? (
                      <>
                        {detailEvent.gameType === 'team' && myTeams.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Komanda seçin</p>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {myTeams.map((team: any) => (
                                <button
                                  key={team.id}
                                  type="button"
                                  onClick={() => setRegisterTeamId(team.id)}
                                  className={cn(
                                    "w-full flex items-center justify-between px-3 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all",
                                    registerTeamId === team.id
                                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300"
                                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-200"
                                  )}
                                >
                                  <span className="flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    {team.name}
                                  </span>
                                  {registerTeamId === team.id && <Check className="w-4 h-4" />}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {detailEvent.gameType === 'team' && myTeams.length === 0 && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                            Bu tədbirə qeydiyyat üçün komandanız olmalıdır.
                          </p>
                        )}
                        <Button
                          onClick={handleRegister}
                          disabled={registrationLoading || (detailEvent.gameType === 'team' && !registerTeamId)}
                          className="w-full"
                        >
                          {registrationLoading ? '...' : 'Bir kliklə qeydiyyatdan keç'}
                        </Button>
                      </>
                    ) : isFeatureEnabled('USER_ACCOUNTS') ? (
                      <AuthLink mode="login">
                        <Button variant="outline" className="w-full gap-2">
                          <ArrowRight className="w-4 h-4" />
                          Qeydiyyat üçün daxil olun
                        </Button>
                      </AuthLink>
                    ) : null}
                  </div>
                ) : detailEvent.registrationLink ? (
                  <a href={safeExternalUrl(detailEvent.registrationLink)} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full gap-2">
                      <ExternalLink className="w-4 h-4" />
                      Qeydiyyat
                    </Button>
                  </a>
                ) : null}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Home
